import React from "react";
import moment from "moment";
import { dateFilterDates } from "../helpers";
import { SelectChangeEvent } from "@mui/material";

export type DateTypes =
  | "today"
  | "yesterday"
  | "lastWeek"
  | "thisWeek"
  | "thisMonth"
  | "lastMonth"
  | "last30Days"
  | "allTimes"
  | "custom";

export interface IDate {
  fromDate: string;
  toDate: string;
}

export const useDateFilter = (
  format = "YYYY-MM-DD",
  defaultType: DateTypes = "today"
) => {
  const {
    today,
    tommorow,
    lastweek,
    startDateOfMonth,
    lastMonthStart,
    lastMonthEnd,
    last30Days,
    yesterday,
    thisWeekStartDate,
    thisWeekEndDate,
    allTimesStartDate
  } = dateFilterDates(format);
  const [date, setDate] = React.useState({
    ...getInitialDate(defaultType, format),
  });
  const [type, setType] = React.useState(defaultType);
  const updateDate = (type: DateTypes) => {
    if (type === "today") {
      setDate({ fromDate: today, toDate: tommorow });
    } else if (type === "yesterday") {
      setDate({ fromDate: yesterday, toDate: yesterday });
    } else if (type === "lastWeek") {
      // Get the start date (Monday) of last week
      const lastWeekStartDate = moment().clone().subtract(1, "week").startOf("isoWeek").format(format);
      // Get the end date (Sunday) of last week
      const lastWeekEndDate = moment().clone().subtract(1, "week").endOf("isoWeek").format(format);
      setDate({ fromDate: lastWeekStartDate, toDate: lastWeekEndDate });
    } else if (type === "thisMonth") {
      setDate({ fromDate: startDateOfMonth, toDate: today });
    } else if (type === "last30Days") {
      setDate({ fromDate: last30Days, toDate: today });
    } else if (type === "thisWeek") {
      setDate({ fromDate: thisWeekStartDate, toDate: thisWeekEndDate });
    } else if (type === "lastMonth") {
      setDate({ fromDate: lastMonthStart, toDate: lastMonthEnd });
    }
    else if (type === "allTimes") {
      setDate({ fromDate: allTimesStartDate, toDate: tommorow });
    }
    setType(type as DateTypes);
  };

  const handleDateSelectChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as DateTypes;
    updateDate(value);
  };

  const dateDropdownList = React.useMemo(() => {
    return [
      { label: "Today", value: "today" },
      { label: "Yesterday", value: "yesterday" },
      { label: "Last Week", value: "lastWeek" },
      { label: "This Week", value: "thisWeek" },
      { label: "This Month", value: "thisMonth" },
      { label: "Last Month", value: "lastMonth" },
      { label: "Last 30 Days", value: "last30Days" },
      { label: "All Times", value: "allTimes" },
      { label: "Customize", value: "custom" },
    ];
  }, []);

  return {
    date,
    setDate,
    type,
    setType,
    handleDateSelectChange,
    updateDate,
    dateDropdownList,
  };
};

const getInitialDate = (type: DateTypes, format?: string) => {
  const {
    today,
    tommorow,
    lastweek,
    startDateOfMonth,
    yesterday,
    lastMonthStart,
    lastMonthEnd,
    last30Days,
    thisWeekStartDate,
    thisWeekEndDate,
    allTimesStartDate,
  } = dateFilterDates(format);
  if (type === "yesterday") {
    return { fromDate: yesterday, toDate: yesterday };
  } else if (type === "lastWeek") {
    // Get the start date (Monday) of last week
    const lastWeekStartDate = moment().clone().subtract(1, "week").startOf("isoWeek").format(format);
    // Get the end date (Sunday) of last week
    const lastWeekEndDate = moment().clone().subtract(1, "week").endOf("isoWeek").format(format);
    return { fromDate: lastWeekStartDate, toDate: lastWeekEndDate };
  } else if (type === "thisMonth") {
    return { fromDate: startDateOfMonth, toDate: today };
  } else if (type === "last30Days") {
    return { fromDate: last30Days, toDate: today };
  } else if (type === "lastMonth") {
    return { fromDate: lastMonthStart, toDate: lastMonthEnd };
  } else if (type === "thisWeek") {
    return { fromDate: thisWeekStartDate, toDate: thisWeekEndDate };
  } else if (type === "allTimes") {
    return { fromDate: allTimesStartDate, toDate: tommorow };
  }
  return { fromDate: today, toDate: tommorow };
};
